import { useEffect, useState } from 'react';
import draftToHtml from 'draftjs-to-html';
import { mapTaskData } from '../utils/taskDataMapper';
import { isTaskCompletedOnTime } from '../utils/taskUtils';

/**
 * Custom hook for managing task data transformation and processing
 * @param {Object} task - Original task object
 * @param {string} type - Action type (PROFILE or CALENDAR)
 * @param {string} machineGroupName - Machine group name
 * @param {string} machineGroupImg - Machine group image
 * @returns {Object} Processed task data and related state
 */
export function useTaskData(task, type, machineGroupName, machineGroupImg) {
  const [taskData, setTaskData] = useState(null);
  const [descHtml, setDescHtml] = useState(null);
  const [isCompletedOnTime, setIsCompletedOnTime] = useState(null);

  useEffect(() => {
    if (!task) {
      setTaskData(null);
      return;
    }

    const mappedData = mapTaskData(task, type, machineGroupName, machineGroupImg);
    setTaskData(mappedData);
  }, [task, type, machineGroupName, machineGroupImg]);

  useEffect(() => {
    if (!taskData) {
      setDescHtml(null);
      setIsCompletedOnTime(null);
      return;
    }

    // Process description HTML
    if (taskData.description) {
      try {
        const parsedDescription = typeof taskData.description === 'string'
          ? JSON.parse(taskData.description)
          : taskData.description;
        setDescHtml(draftToHtml(parsedDescription));
      } catch (error) {
        // Description is plain text, not Draft.js JSON - this is expected
        // Just set it as null, the plain text description will be handled elsewhere
        setDescHtml(null);
      }
    } else {
      setDescHtml(null);
    }

    // Check if task was completed on time
    if (taskData.maintenanceName !== 'Report a repair') {
      const completedOnTime = isTaskCompletedOnTime(
        taskData.updatedAt,
        taskData.createdAt,
        taskData.maintenanceName
      );
      setIsCompletedOnTime(completedOnTime);
    } else {
      setIsCompletedOnTime(null);
    }
  }, [taskData]);

  return {
    taskData,
    descHtml,
    isCompletedOnTime,
  };
}